import { User, MapPin, ClockCountdown, CaretRight, Truck, Receipt, Tray } from "@phosphor-icons/react";
import { formatDateTime } from "../../lib/api";

const fmt = (n) => (Number.isInteger(n) ? n : Number(n).toFixed(2).replace(/\.00$/, ""));
const TONES = {
  amber: "from-brand-500/15 text-brand-700 ring-brand-200",
  emerald: "from-emerald-500/15 text-emerald-700 ring-emerald-200",
  sky: "from-sky-500/15 text-sky-700 ring-sky-200",
  slate: "from-slate-500/10 text-slate-700 ring-slate-200",
};

export const Kpi = ({ label, value, tone, testid }) => (
  <div className={`rounded-2xl bg-gradient-to-br ${TONES[tone]} to-white ring-1 px-4 py-3.5`} data-testid={testid}>
    <div className="text-[10px] font-bold uppercase tracking-[0.16em] opacity-80">{label}</div>
    <div className="font-heading text-3xl font-extrabold text-slate-900 mt-0.5">{value}</div>
  </div>
);

export const Empty = ({ text }) => (
  <div className="py-14 text-center text-sm text-slate-400 flex flex-col items-center gap-2" data-testid="portal-empty"><Tray size={34} weight="duotone" /> {text}</div>
);

export const PartyRow = ({ x, kind, onClick }) => {
  const pending = x.dispatch_pending_total || 0;
  const last = (x.dispatches || []).at(-1);
  return (
    <button type="button" data-testid={`party-row-${x.id}`} onClick={onClick}
      className="group w-full text-left rounded-xl border border-slate-200 bg-white px-4 py-3.5 flex items-center gap-3 hover:border-brand-400 hover:shadow-[0_8px_24px_-12px_rgba(234,179,8,0.5)] transition-[border-color,box-shadow,transform] active:scale-[0.99]">
      <span className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${kind === "sent" ? "bg-emerald-50 text-emerald-600 ring-1 ring-emerald-200" : "bg-brand-50 text-brand-600 ring-1 ring-brand-200"}`}>
        <User size={20} weight="duotone" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-[15px] text-slate-900 truncate">{x.customer_name || x.text}</div>
        {x.customer_address && <div className="text-[11px] text-slate-500 truncate flex items-center gap-1"><MapPin size={11} className="shrink-0" /> {x.customer_address}</div>}
        <div className="text-[10px] text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
          <span className="flex items-center gap-0.5"><ClockCountdown size={11} /> {kind === "sent" && last ? `${last.dispatch_date?.split("-").reverse().join("/")} ${last.dispatch_time || ""}` : formatDateTime(x.created_at)}</span>
          {kind === "sent" && last?.dispatch_no && <span className="font-mono font-bold text-slate-700">{last.dispatch_no}</span>}
          {pending > 0 ? <span className="rounded-full bg-brand-100 text-brand-800 font-bold px-2 py-0.5">{fmt(pending)} pending</span>
            : <span className="rounded-full bg-emerald-100 text-emerald-800 font-bold px-2 py-0.5">Fully sent</span>}
        </div>
      </div>
      <CaretRight size={18} className="text-slate-300 group-hover:text-brand-500 group-hover:translate-x-0.5 transition-transform shrink-0" />
    </button>
  );
};

export const RecordRow = ({ r, onClick }) => {
  const qty = (r.items || []).reduce((a, i) => a + Number(i.qty || 0), 0);
  return (
    <button type="button" data-testid={`record-row-${r.id}`} onClick={onClick}
      className="w-full text-left rounded-xl border border-slate-200 bg-white px-4 py-3 grid grid-cols-[auto_1fr_auto] md:grid-cols-[auto_1.2fr_1fr_1fr_auto] items-center gap-3 hover:border-slate-400 transition-colors">
      <span className="w-10 h-10 rounded-lg bg-slate-900 text-brand-400 flex items-center justify-center"><Receipt size={18} weight="duotone" /></span>
      <div className="min-w-0">
        <div className="font-mono text-xs font-bold text-brand-700">{r.dispatch_no}</div>
        <div className="font-semibold text-sm truncate">{r.customer_name}</div>
      </div>
      <div className="hidden md:flex items-center gap-1.5 text-xs font-mono text-slate-600"><Truck size={14} /> {r.vehicle_no} <span className="font-sans text-slate-400">· {r.driver_name}</span></div>
      <div className="hidden md:block text-xs text-slate-500">{(r.items || []).length} items · {fmt(qty)} units</div>
      <div className="text-right text-[11px] font-mono text-slate-500 leading-tight">{r.dispatch_date?.split("-").reverse().join("/")}<br />{r.dispatch_time}</div>
    </button>
  );
};
